import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from 'app/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'app/components/ui/table';
import { Pencil, Trash, Tag, Loader2 } from 'lucide-react';
import { useProductsSlice } from 'app/pages/Product/slice';
import { AlertProductAttributeValueDelete } from '../delete/delete-confirmation';

const ViewList: React.FC<{ onEdit: (attribute: any) => void }> = ({
  onEdit,
}) => {
  const { id } = useParams();
  const [attributeToDelete, setAttributeToDelete] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const { useLazyGetSingleProductsAttributeTypeQuery } = useProductsSlice();
  const [getSingleValue, { data, isError, isLoading }] =
    useLazyGetSingleProductsAttributeTypeQuery();

  useEffect(() => {
    if (id) {
      getSingleValue(id);
    }
  }, [id, getSingleValue]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center space-y-3">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            <p className="text-sm text-gray-600 dark:text-gray-400">Loading attribute values...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center space-y-3">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                <div className="w-4 h-4 bg-white rounded-full"></div>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Error Loading Data</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center max-w-sm">
              There was an error loading the attribute values. Please try refreshing the page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const attributes = data?.data || [];

  return (
    <div className="p-6">
      {/* Empty State */}
      {attributes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-full flex items-center justify-center mb-4">
            <Tag className="w-8 h-8 text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No Values Found
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 max-w-sm">
            Start by creating your first attribute value to organize your products better.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <Table>
            <TableHeader className="bg-gray-50 dark:bg-gray-800/50">
              <TableRow className="border-gray-200 dark:border-gray-700">
                <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-left w-auto">
                  Value Name
                </TableHead>
                <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-left w-auto">
                  Description
                </TableHead>
                <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-left w-auto">
                  Slug
                </TableHead>
                <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-center w-32">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attributes.map(item => (
                <TableRow
                  key={item.id}
                  className="border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors duration-200"
                >
                  <TableCell className="py-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Tag className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      </div>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {item.name}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                      {item.description}
                    </p>
                  </TableCell>
                  <TableCell className="py-4">
                    <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-gray-700 dark:text-gray-300">
                      {item.slug}
                    </code>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex items-center justify-center space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onEdit(item)}
                        className="h-8 w-8 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700"
                      >
                        <Pencil className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setAttributeToDelete(item);
                          setOpenDelete(true);
                        }}
                        className="h-8 w-8 p-0 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300 dark:hover:border-red-700"
                      >
                        <Trash className="w-4 h-4 text-red-600 dark:text-red-400" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {attributeToDelete && (
        <AlertProductAttributeValueDelete
          isOpen={openDelete}
          onOpenChange={isOpen => setOpenDelete(isOpen)}
          data={attributeToDelete}
        />
      )}
    </div>
  );
};

export default ViewList;
